import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Sensor, SensorGroup, SensorReadingType } from '@models';
import { ChartDetails } from '@models/ChartFavorite/chartDetails.model';
import { ChartFavoriteInfo } from '@models/ChartFavorite/chartfavoriteInfo.model';
import { DashboardInfo } from '@models/Dashboard/dashboardInfo.model';
import { DashboardWizardState } from '@models/Dashboard/dashboardWizard.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { SensorReadingCriteria2 } from '@models/Sensor/sensor.model';
import { SensorWithGroupList } from '@models/Sensor/sensorWithGroupList.model';
import { UserGroup } from '@models/User/usergroup.model';
import { DateRanges } from '@models/ranges';
import { SensorService } from '@services/sensor.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { SensorReadingsService } from '../../../sensor-readings/sensor-readings.service';
import { CommonShared } from 'app/shared/classes/common';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

// The data the container feeds to the right-pane live preview chart (the full
// chart-readings-echart-modal, same inputs as the review-readings Chart tab).
export interface WizardPreviewInputs {
  sensors: Sensor[];
  sensorReadingTypes: SensorReadingType[];
  sensorReadings: SensorReadingInfo[];
  sensorGroups: SensorGroup[];
  dateRangeStart: Date;
  includeCalValues: boolean;
  includeOrigValues: boolean;
  darkBackground: boolean;
  // Restored chart options (type, colors, aggregates, ...) when editing a favorite.
  savedChartDetails: ChartDetails;
}

// The Chart Readings flow: steps 2-5 of the create wizard. Steps 2-4 reuse the
// "Analyze Sensor Readings" (review-readings) controls and its real readings-
// loading pipeline (copy-adapted here, trimmed of GPS/condition-filter/favorites)
// so the right-pane chart fills with REAL data as the user makes selections.
// Save (step 5 Finish) is deferred for this pass.
@Component({
  standalone: false,
  selector: 'chart-favorite-wizard-flow',
  templateUrl: './chart-favorite-wizard-flow.component.html',
  styleUrls: ['./chart-favorite-wizard-flow.component.scss']
})
export class ChartFavoriteWizardFlowComponent implements OnInit, OnChanges {
  @Input() step = 2;
  @Input() state: DashboardWizardState;
  @Input() dashboards: DashboardInfo[] = [];
  @Input() associatedUserGroups: UserGroup[] = [];
  @Input() username = '';
  // When set, the wizard edits this existing favorite (pre-load its values).
  @Input() editFavorite: ChartFavoriteInfo = null;
  @Input() isEditMode = false;

  @Output() stepValidChange = new EventEmitter<boolean>();
  @Output() previewChanged = new EventEmitter<WizardPreviewInputs>();
  @Output() finished = new EventEmitter<number>();

  common = new CommonShared();

  // Reference data
  allSensors: Sensor[] = [];
  allSensorGroups: SensorGroup[] = [];
  sensorsLoaded = false;

  // Sensors and Groups tree (step 2)
  sensorsAndGroupsDisplayed: SensorWithGroupList[] = [];
  SensorsAndGroupsCols = [
    { field: 'checked', header: '', width: '10%' },
    { field: 'name', header: 'Sensors and Groups', width: '90%' }
  ];
  searchSensorsText = '';

  // Reading Types list (step 3)
  allAvailableReadingTypes: SensorReadingType[] = [];
  readingTypesDisplayed: SensorReadingType[] = [];
  SensorReadingTypesCols = [
    { field: 'checked', header: '', width: '10%' },
    { field: 'name', header: 'Available Sensor Reading Types', width: '90%' }
  ];
  searchReadingTypeText = '';

  // Date range (step 4)
  ranges = DateRanges;

  // ngx-select-dropdown config for the step-5 access-group pickers.
  readonly userGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '220px',
    placeholder: 'Select Access Group',
    searchOnKey: 'name'
  };

  // Real-readings pipeline
  private readingsCacheByTypeId = new Map<number, SensorReadingInfo[]>();
  allAvailableReadings: SensorReadingInfo[] = [];
  selectedSensorReadings: SensorReadingInfo[] = [];
  sensorsReady = false;
  datesReady = true;              // a default range ("Last 3 Days") is always present
  private rangeJustClicked = false;

  // Cached echart dateRangeStart, recomputed only when the start ms changes, to
  // avoid a child ngOnChanges rebuild on every emit.
  private _dateRangeStart: Date = null;
  private _dateRangeStartMs = -1;

  // Existing chart-favorite names (lower-cased), for the step-5 collision check.
  private existingFavoriteNames: string[] = [];

  // Edit-mode: restored echart options handed to the preview chart, and the
  // bookkeeping to apply a favorite once reference data has loaded.
  savedChartDetails: ChartDetails = null;
  private groupsLoaded = false;
  private editFavoriteApplied = false;
  private pendingFavoriteTypeIds: number[] = null;

  constructor(
    private sensorService: SensorService,
    private sensorGroupService: SensorGroupService,
    private sensorReadingsService: SensorReadingsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadSensorsAndGroups();
    this.loadChartFavoriteNames();
    this.recomputeValidity();
    this.emitPreview();
  }

  private loadChartFavoriteNames(): void {
    this.sensorReadingsService.getChartFavorites().subscribe(
      (favorites: any[]) => {
        this.existingFavoriteNames = (favorites || []).map(f => (f.name || '').trim().toLowerCase());
        this.recomputeValidity();
      },
      () => { /* non-fatal: the server still enforces favorite-name uniqueness on save */ }
    );
  }

  // True when the entered Chart Favorite name already exists (case-insensitive).
  // When editing, the favorite's own current name is allowed (not a collision).
  get favoriteNameTaken(): boolean {
    const name = (this.cf.favoriteName || '').trim().toLowerCase();
    if (name === '') { return false; }
    if (this.isEditMode && this.editFavorite && name === (this.editFavorite.name || '').trim().toLowerCase()) {
      return false;
    }
    return this.existingFavoriteNames.includes(name);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step'] && !changes['step'].firstChange) {
      this.recomputeValidity();
    }
  }

  // Shorthand to the chart-favorite slice of the shared wizard state.
  get cf() {
    return this.state.chartFavorite;
  }

  // True when "Add to new dashboard" is chosen and the entered name collides with
  // an existing dashboard (case-insensitive). Drives the red input + disabled Finish.
  get newDashboardNameTaken(): boolean {
    const name = (this.cf.newDashboardName || '').trim().toLowerCase();
    if (this.cf.dashboardTargetMode !== 'new' || name === '') {
      return false;
    }
    return this.dashboards.some(d => d.name && d.name.trim().toLowerCase() === name);
  }

  // ---------- data loading ----------

  private loadSensorsAndGroups(): void {
    this.sensorService.getAllSensorsWithoutTypes().subscribe(
      (results: Sensor[]) => {
        if (results != null) {
          this.allSensors = results;
          this.allSensors.forEach(s => { if (!s.name) { s.name = s.uuid; } });
        }
        this.sensorsLoaded = true;
        this.onReferenceDataLoaded();
      },
      () => { this.sensorsLoaded = true; this.toastr.error('Error occurred while retrieving sensor list'); }
    );

    this.sensorGroupService.getSensorGroupsWithSensors().subscribe(
      (results: SensorGroup[]) => {
        if (results != null) { this.allSensorGroups = results; }
        this.groupsLoaded = true;
        this.onReferenceDataLoaded();
      },
      () => { this.groupsLoaded = true; this.toastr.error('Error occurred while retrieving sensor groups'); }
    );
  }

  // Build the tree once reference data arrives. In edit mode, wait for BOTH
  // sensors and groups so a favorite's group memberships resolve, then pre-load it.
  private onReferenceDataLoaded(): void {
    if (this.editFavorite != null && !this.editFavoriteApplied) {
      if (this.sensorsLoaded && this.groupsLoaded) {
        this.editFavoriteApplied = true;
        this.applyEditFavorite();
      }
      return;
    }
    this.sensorSearchTextChanged();
  }

  // ---------- edit mode: pre-load an existing favorite ----------

  // Replicates review-readings' applyFavorite: seed the sensors/groups tree with
  // the favorite's selections checked, restore date range + chart options, then
  // load reading types (which, on return, re-check the favorite's types and fetch).
  private applyEditFavorite(): void {
    const fav = this.editFavorite;

    const groups = (fav.sensorGroups != undefined && fav.sensorGroups.length > 0)
      ? fav.sensorGroups
      : (fav.sensorGroup != undefined && fav.sensorGroup != null ? [fav.sensorGroup] : []);
    const groupIds = groups.map(g => g.id);
    const sensorIds = (fav.sensors ?? []).map(s => s.id);

    const rows: SensorWithGroupList[] = [];
    const sensorIdsInSelectedGroups = new Set<number>();

    groupIds.forEach(id => {
      const group = this.allSensorGroups.find(x => x.id === id);
      if (group) {
        const groupSensorIds = (group.sensors ?? []).map(s => s.id);
        const checkedIds = groupSensorIds.filter(sid => sensorIds.includes(sid));
        const row = this.makeGroupRow(group, checkedIds);
        row.expanded = row.indeterminate === true;
        rows.push(row);
        groupSensorIds.forEach(sid => sensorIdsInSelectedGroups.add(sid));
      }
    });

    sensorIds.forEach(id => {
      const sensor = this.allSensors.find(x => x.id === id);
      if (sensor && !sensorIdsInSelectedGroups.has(id)) {
        rows.push({ id: sensor.id, name: sensor.name, isSensor: true, isSensorGroup: false, checked: true, count: 1 });
      }
    });

    rows.sort((a, b) => a.name.localeCompare(b.name));
    this.sensorsAndGroupsDisplayed = rows;
    this.updateSelectedSensors();
    this.sensorsReady = this.cf.selectedSensors.length > 0;

    this.applyFavoriteChartDetails(fav.chartDetails);
    this.applyFavoriteDateRange(fav.dateRange);
    this.datesReady = true;

    this.cf.favoriteName = fav.name;
    this.cf.favoriteUserGroup = this.associatedUserGroups.find(g => g.id === fav.userGroupId) || null;

    this.invalidateReadingsCache();
    this.pendingFavoriteTypeIds = (fav.sensorReadingTypes ?? []).map(t => t.id);
    this.getReadingTypesForSensors(this.cf.selectedSensors.map(s => s.id));
    this.recomputeValidity();
  }

  // Parse the favorite's chartDetails JSON: apply host-level fields (timezone,
  // reading source) to wizard state and hand the whole object to the preview
  // chart via savedChartDetails (it restores chart type, colors, aggregates, ...).
  private applyFavoriteChartDetails(chartDetailsJson: string): void {
    this.savedChartDetails = null;
    let details: ChartDetails = null;
    try {
      details = (chartDetailsJson != undefined && chartDetailsJson !== '') ? JSON.parse(chartDetailsJson) : null;
    } catch {
      details = null;
    }
    if (details == null) {
      this.savedChartDetails = { echartType: 'line' };
      return;
    }
    if (details.timeZone != undefined) {
      const [tz, offset] = this.common.setTimeZoneSelection(details.timeZone);
      this.cf.selectedTimeZone = tz;
      this.cf.selectedTimeZoneOffset = offset;
    }
    if (details.showOriginalReadings != undefined) { this.cf.showOriginalReadings = details.showOriginalReadings; }
    if (details.showCalibratedReadings != undefined) { this.cf.showCalibratedReadings = details.showCalibratedReadings; }
    this.savedChartDetails = details;
  }

  // Decode a stored date range (preset label or "start,end" ISO) — inverse of the
  // encoding review-readings/this wizard use when saving.
  private applyFavoriteDateRange(dateRange: string): void {
    if (dateRange == undefined || dateRange === '') { return; }
    if ((this.ranges as any)[dateRange] != undefined) {
      let range: any;
      if (dateRange === 'Today') { range = [moment().startOf('day'), moment().endOf('day')]; }
      else if (dateRange === 'Yesterday') { range = [moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')]; }
      else { range = (this.ranges as any)[dateRange]; }
      this.cf.dateRange = { startDate: range[0], endDate: range[1] };
      this.cf.dateRangeName = dateRange;
    } else {
      const parts = dateRange.split(',');
      this.cf.dateRange = { startDate: moment(parts[0]), endDate: moment(parts[1]) };
      this.cf.dateRangeName = 'Custom Range';
    }
  }

  // ---------- step 2: Sensors and Groups ----------

  onPresetChanged(): void {
    this.sensorSearchTextChanged();
  }

  // Base sensors the search operates over, after the scope presets. "All" wins
  // (no filter); otherwise the union of the enabled scopes; with no scope at all,
  // nothing until the user picks one.
  private scopedSensors(): Sensor[] {
    const p = this.cf.sensorPreset;
    if (p.allSensorsAndGroups) { return this.allSensors; }
    if (!p.assignedToMeOrGroup && !p.recentlyAddedOrModified) { return []; }
    return this.allSensors.filter(s => this.matchesScope(s.userName, s.userGroupId, s.modifiedDate));
  }

  private scopedGroups(): SensorGroup[] {
    const p = this.cf.sensorPreset;
    if (p.allSensorsAndGroups) { return this.allSensorGroups; }
    if (!p.assignedToMeOrGroup && !p.recentlyAddedOrModified) { return []; }
    return this.allSensorGroups.filter(g => this.matchesScope(g.userName, g.userGroupId, g.modifiedDate));
  }

  private matchesScope(userName: string, userGroupId: number, modifiedDate: Date): boolean {
    const p = this.cf.sensorPreset;
    let keep = false;
    if (p.assignedToMeOrGroup) {
      const mine = userName && this.username && userName.toLowerCase() === this.username.toLowerCase();
      const inGroup = this.associatedUserGroups.some(g => g.id === userGroupId);
      keep = keep || mine || inGroup;
    }
    if (p.recentlyAddedOrModified) {
      // Sensors have no created-date; modifiedDate within 30 days is the signal.
      keep = keep || (modifiedDate ? moment(modifiedDate).isAfter(moment().subtract(30, 'days')) : false);
    }
    return keep;
  }

  // Rebuild the displayed tree from the scoped sensors/groups + search text,
  // keeping any already-checked rows (and partially-checked groups) pinned.
  sensorSearchTextChanged(): void {
    const isSelected = (row: SensorWithGroupList) =>
      row.checked || (row.isSensorGroup && (row.children ?? []).some(c => c.checked));
    const kept = this.sensorsAndGroupsDisplayed.filter(isSelected);
    const keptSensorIds = new Set(kept.filter(r => r.isSensor).map(r => r.id));
    const keptGroupIds = new Set(kept.filter(r => r.isSensorGroup).map(r => r.id));

    const search = (this.searchSensorsText || '').trim().toLowerCase();
    this.searchSensorsText = search;

    let sensors = this.scopedSensors().filter(x =>
      (x.name || '').toLowerCase().includes(search) || (x.uuid || '').toLowerCase().includes(search));
    sensors = sensors.slice(0, 500).filter(x => !keptSensorIds.has(x.id));

    let groups = this.scopedGroups().filter(x => (x.name || '').toLowerCase().includes(search));
    groups = groups.slice(0, 150).filter(x => !keptGroupIds.has(x.id));

    this.sensorsAndGroupsDisplayed = kept
      .concat(sensors.map(s => ({ id: s.id, name: s.name, isSensor: true, isSensorGroup: false, checked: false, count: 1 })))
      .concat(groups.map(g => this.makeGroupRow(g, [])));

    this.sensorsAndGroupsDisplayed.sort((a, b) => a.name.localeCompare(b.name));
  }

  // Build a Sensors-and-Groups row for a group, with a child row per member sensor.
  private makeGroupRow(group: SensorGroup, checkedSensorIds: number[]): SensorWithGroupList {
    const children: SensorWithGroupList[] = (group.sensors ?? []).map(s => ({
      id: s.id,
      name: s.name,
      isSensor: true,
      isSensorGroup: false,
      checked: checkedSensorIds.includes(s.id),
      count: 1
    }));
    const checkedCount = children.filter(c => c.checked).length;
    return {
      id: group.id,
      name: group.name,
      isSensor: false,
      isSensorGroup: true,
      count: group.sensors != undefined ? group.sensors.length : 0,
      children,
      expanded: false,
      checked: children.length > 0 && checkedCount === children.length,
      indeterminate: checkedCount > 0 && checkedCount < children.length
    };
  }

  onGroupChecked(group: SensorWithGroupList): void {
    (group.children ?? []).forEach(c => c.checked = group.checked);
    group.indeterminate = false;
    this.sensorSelectionChanged();
  }

  onGroupChildChecked(group: SensorWithGroupList): void {
    const children = group.children ?? [];
    const checkedCount = children.filter(c => c.checked).length;
    group.checked = children.length > 0 && checkedCount === children.length;
    group.indeterminate = checkedCount > 0 && checkedCount < children.length;
    this.sensorSelectionChanged();
  }

  toggleGroupExpanded(group: SensorWithGroupList): void {
    group.expanded = !group.expanded;
  }

  groupCheckedCount(group: SensorWithGroupList): number {
    return (group.children ?? []).filter(c => c.checked).length;
  }

  toggleCheckedSensors(): void {
    if (this.sensorsAndGroupsDisplayed.length === 0) { return; }
    const newState = !this.sensorsAndGroupsDisplayed[0].checked;
    this.sensorsAndGroupsDisplayed.forEach(row => {
      row.checked = newState;
      if (row.isSensorGroup) {
        row.indeterminate = false;
        (row.children ?? []).forEach(c => c.checked = newState);
      }
    });
    this.sensorSelectionChanged();
  }

  sensorSelectionChanged(): void {
    this.updateSelectedSensors();
    this.invalidateReadingsCache();
    this.getReadingTypesForSensors(this.cf.selectedSensors.map(s => s.id));
    this.sensorsReady = this.cf.selectedSensors.length > 0;
    this.recomputeSelectedReadings();
    this.recomputeValidity();
  }

  // Rebuild selectedSensors/selectedSensorGroups from the tree, deduped (a sensor
  // may be checked both individually and via a group).
  private updateSelectedSensors(): void {
    const selectedSensors: Sensor[] = [];
    const selectedSensorGroups: SensorGroup[] = [];

    const addSensor = (id: number) => {
      const sensor = this.allSensors.find(s => s.id === id);
      if (sensor && !selectedSensors.some(s => s.id === sensor.id)) {
        sensor.displayedSensorName = this.common.getSensorDisplayName(sensor);
        selectedSensors.push(sensor);
      }
    };

    this.sensorsAndGroupsDisplayed.forEach(row => {
      if (row.isSensor && row.checked) {
        addSensor(row.id);
      }
      if (row.isSensorGroup) {
        const checkedChildren = (row.children ?? []).filter(c => c.checked);
        if (checkedChildren.length > 0) {
          const group = this.allSensorGroups.find(g => g.id === row.id);
          if (group) { selectedSensorGroups.push(group); }
          checkedChildren.forEach(c => addSensor(c.id));
        }
      }
    });

    this.cf.selectedSensors = selectedSensors;
    this.cf.selectedSensorGroups = selectedSensorGroups;
  }

  // ---------- step 3: Reading Types ----------

  getReadingTypesForSensors(sensorIds: number[]): void {
    if (sensorIds.length === 0) {
      this.allAvailableReadingTypes = [];
      this.readingTypesDisplayed = [];
      this.cf.availableReadingTypes = [];
      return;
    }
    this.sensorService.getReadingTypesForSensors(sensorIds, '').subscribe(
      (results: SensorReadingType[]) => {
        this.allAvailableReadingTypes = results ?? [];
        this.common.sortArrayByTypeParm(this.allAvailableReadingTypes);
        // Pre-check the favorite's saved types when pre-loading an edit; otherwise
        // start unchecked. (The types list only arrives async, so a favorite's
        // selections can only be re-checked here.)
        if (this.pendingFavoriteTypeIds != null) {
          const want = new Set(this.pendingFavoriteTypeIds);
          this.allAvailableReadingTypes.forEach(t => t.checked = want.has(t.id));
          this.pendingFavoriteTypeIds = null;
        } else {
          this.allAvailableReadingTypes.forEach(t => t.checked = false);
        }
        this.readingTypesDisplayed = this.allAvailableReadingTypes;
        this.cf.availableReadingTypes = this.allAvailableReadingTypes;
        this.recomputeSelectedReadings();
        // Fetch readings for any checked types (a no-op when none are checked).
        if (this.sensorsReady && this.datesReady) {
          this.loadReadings();
        }
        this.recomputeValidity();
      },
      () => this.toastr.error('Error occurred while retrieving sensor data types')
    );
  }

  readingTypeSearchTextChanged(): void {
    const search = (this.searchReadingTypeText || '').trim().toLowerCase();
    this.searchReadingTypeText = search;
    this.readingTypesDisplayed = this.allAvailableReadingTypes.filter(x => x.type.toLowerCase().includes(search));
  }

  readingTypeSelectionChanged(): void {
    this.recomputeSelectedReadings();
    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
    this.recomputeValidity();
  }

  toggleCheckedReadings(): void {
    if (this.readingTypesDisplayed.length === 0) { return; }
    const newState = !this.readingTypesDisplayed[0].checked;
    this.readingTypesDisplayed.forEach(r => r.checked = newState);
    this.readingTypeSelectionChanged();
  }

  // ---------- step 4: date range + display options ----------

  datesUpdated(quickPick: string): void {
    if (quickPick === 'Last 4 hrs')       { this.cf.dateRange = { startDate: moment().add(-4, 'hours'),  endDate: moment() }; }
    else if (quickPick === 'Last 8 hrs')  { this.cf.dateRange = { startDate: moment().add(-8, 'hours'),  endDate: moment() }; }
    else if (quickPick === 'Last 12 hrs') { this.cf.dateRange = { startDate: moment().add(-12, 'hours'), endDate: moment() }; }
    else if (quickPick === 'Last 24 hrs') { this.cf.dateRange = { startDate: moment().add(-24, 'hours'), endDate: moment() }; }

    if (quickPick !== '') {
      this.cf.dateRangeName = quickPick;
    } else if (this.rangeJustClicked) {
      this.rangeJustClicked = false;   // keep the preset label set by setRangeSelected()
    } else {
      this.cf.dateRangeName = 'Custom Range';
    }

    this.datesReady = true;
    this.invalidateReadingsCache();
    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
    this.emitPreview();
  }

  setRangeSelected(selectedRange: any): void {
    this.cf.dateRangeName = selectedRange.label;
    this.rangeJustClicked = true;
    if (selectedRange.dates != undefined && selectedRange.dates.length === 2) {
      this.cf.dateRange = { startDate: selectedRange.dates[0], endDate: selectedRange.dates[1] };
    }
    this.invalidateReadingsCache();
    this.emitPreview();
  }

  selectedTimeZoneChanged(): void {
    const [tz, offset] = this.common.setTimeZoneSelection(this.cf.selectedTimeZone);
    this.cf.selectedTimeZone = tz;
    this.cf.selectedTimeZoneOffset = offset;
    this.invalidateReadingsCache();
    if (this.sensorsReady && this.datesReady) {
      this.loadReadings();
    }
    this.emitPreview();
  }

  // Original/Calibrated toggles only change which value column the chart reads;
  // both are returned in every reading, so no refetch is needed.
  selectedReadingSourceChanged(): void {
    this.emitPreview();
  }

  // ---------- real readings pipeline (copy-adapted from review-readings) ----------

  loadReadings(): void {
    if (this.cf.selectedSensors.length === 0 || this.cf.selectedReadingTypes.length === 0) {
      return;
    }

    const missingTypes = this.cf.selectedReadingTypes.filter(rt => !this.readingsCacheByTypeId.has(rt.id));

    if (missingTypes.length === 0) {
      this.rebuildAllAvailableReadings();
      this.recomputeSelectedReadings();
      return;
    }

    const criteria: SensorReadingCriteria2 = {
      sensorIds: this.cf.selectedSensors.map(s => s.id),
      startDate: this.cf.dateRange.startDate.format('YYYY-MM-DD HH:mm'),
      endDate: this.cf.dateRange.endDate.format('YYYY-MM-DD HH:mm'),
      timeZoneDisplay: this.cf.selectedTimeZone,
      timeZoneOffset: this.cf.selectedTimeZoneOffset,
      sensorReadingTypes: missingTypes
    };

    this.sensorService.getSensorReadingsForSensor(criteria).subscribe(
      (results: SensorReadingInfo[]) => {
        const sensorNameMap = new Map<number, string>();
        this.cf.selectedSensors.forEach(s => sensorNameMap.set(s.id, this.common.getSensorDisplayName(s)));

        const loaded: SensorReadingInfo[] = (results ?? []).filter(
          s => s.value != 'nan' && s.value != 'NaN' && s.origValue != 'nan' && s.origValue != 'NaN');
        loaded.forEach(reading => {
          reading.createdDate = new Date(reading.createdDate);
          reading.sensorName = sensorNameMap.get(reading.sensorID) || '';
          reading.value = Number(reading.value).toPrecision(9);
          reading.origValue = Number(reading.origValue).toPrecision(9);
        });

        // Seed every requested type (so an empty type is not re-requested).
        missingTypes.forEach(rt => this.readingsCacheByTypeId.set(rt.id, []));
        loaded.forEach(reading => {
          const cached = this.readingsCacheByTypeId.get(reading.typeID);
          if (cached != undefined) { cached.push(reading); }
          else { this.readingsCacheByTypeId.set(reading.typeID, [reading]); }
        });

        this.rebuildAllAvailableReadings();
        this.recomputeSelectedReadings();

        if (loaded.length > 0) {
          this.toastr.success(loaded.length + ' sensor readings loaded successfully');
        }
      },
      err => this.toastr.error('Error occurred while retrieving sensor readings. Error: ' + err.message)
    );
  }

  // Discard cached readings and refetch (bound to the Reading Types sync icon).
  refreshReadings(): void {
    this.invalidateReadingsCache();
    this.loadReadings();
  }

  private invalidateReadingsCache(): void {
    this.readingsCacheByTypeId.clear();
    this.allAvailableReadings = [];
  }

  // Flatten the per-type cache, newest-first, re-indexing id (unique row key).
  private rebuildAllAvailableReadings(): void {
    const merged: SensorReadingInfo[] = [];
    this.readingsCacheByTypeId.forEach(readings => {
      for (const reading of readings) { merged.push(reading); }
    });
    merged.sort((a, b) => {
      if (b.createdDate > a.createdDate) { return 1; }
      if (b.createdDate < a.createdDate) { return -1; }
      return 0;
    });
    merged.forEach((reading, index) => reading.id = index);
    this.allAvailableReadings = merged;
  }

  // Recompute selected reading types (from checkbox state) and the readings that
  // feed the chart, then push a fresh preview.
  private recomputeSelectedReadings(): void {
    this.cf.selectedReadingTypes = this.allAvailableReadingTypes.filter(x => x.checked);
    this.selectedSensorReadings = this.allAvailableReadings.filter(reading =>
      this.cf.selectedReadingTypes.some(type => type.id === reading.typeID) &&
      this.cf.selectedSensors.some(sensor => sensor.id === reading.sensorID));
    this.emitPreview();
  }

  // ---------- step 5: save fields ----------

  onSaveFieldChanged(): void {
    this.recomputeValidity();
  }

  // ---------- preview ----------

  private emitPreview(): void {
    const start = this.cf.dateRange.startDate.toDate();
    const startMs = start.getTime();
    if (startMs !== this._dateRangeStartMs) {
      this._dateRangeStartMs = startMs;
      this._dateRangeStart = start;
    }
    this.previewChanged.emit({
      sensors: this.cf.selectedSensors,
      sensorReadingTypes: this.cf.selectedReadingTypes,
      sensorReadings: this.selectedSensorReadings,
      sensorGroups: this.cf.selectedSensorGroups,
      dateRangeStart: this._dateRangeStart,
      includeCalValues: this.cf.showCalibratedReadings,
      includeOrigValues: this.cf.showOriginalReadings,
      darkBackground: false,
      savedChartDetails: this.savedChartDetails
    });
  }

  // ---------- validation (gates the container's Next / Finish) ----------

  private recomputeValidity(): void {
    let valid = false;
    switch (this.step) {
      case 2:
        valid = this.cf.selectedSensors.length > 0;
        break;
      case 3:
        valid = this.cf.selectedReadingTypes.length > 0;
        break;
      case 4:
        valid = this.cf.dateRange != null;
        break;
      case 5:
        if (this.isEditMode) {
          // Editing only updates the favorite — just a valid, non-colliding name.
          valid = !!this.cf.favoriteName && this.cf.favoriteName.trim().length > 0 && !this.favoriteNameTaken;
        } else {
          valid = !!this.cf.favoriteName && this.cf.favoriteName.trim().length > 0 && !this.favoriteNameTaken
            && (this.cf.dashboardTargetMode === 'existing'
              ? this.cf.targetDashboard != null
              : !!this.cf.newDashboardName && this.cf.newDashboardName.trim().length > 0 && !this.newDashboardNameTaken);
        }
        break;
      default:
        valid = true;
    }
    this.stepValidChange.emit(valid);
  }
}
